import { nanoid } from 'nanoid';
import * as React from 'react';
import { FormGroup, FormControl } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import type {
	SelectComponentsConfig,
	OptionProps,
	OnChangeValue,
	PropsValue,
	GroupBase,
	StylesConfig,
	Theme,
} from 'react-select';
import { change } from 'redux-form';
import type { WrappedFieldArrayProps } from 'redux-form';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import Label from 'af-components/LockedValue/Label';

export interface OwnProps<T> {
	addonComponent?: () => React.JSX.Element | string;
	className?: string;
	containerClassName?: string;
	disableErrorMessage?: boolean;
	getMultiValueLabelClassName?: (data: T) => string;
	getOptionLabel?: (option: T) => string;
	getOptionValue?: (option: T) => string;
	isDisabled?: boolean;
	label?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	MultiValueLabel?: React.ComponentType<any>;
	onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
	onChange?: (items: T[]) => void;
	onValueChange?: (selectedOption: OnChangeValue<T, true>) => Promise<void>;
	Option?: React.ComponentType<OptionProps<T, true>>;
	options: T[];
	menuPlacement?: 'auto' | 'top' | 'bottom';
	placeholder?: string;
	valueKey?: string;
	fixed?: boolean;
}

export interface MultiTagOptionProps<T> extends OptionProps<T, boolean> {
	data: T;
	onRemove?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

const connector = connect(null, { changeField: change });

type ConnectOwnProps<T> = OwnProps<T> & WrappedFieldArrayProps<T>;
type Props<T> = ConnectOwnProps<T> & ConnectedProps<typeof connector>;

interface DropdownStyle {
	top: number | 'auto';
	bottom: number | 'auto';
	left: number;
	width: number;
}

interface State {
	id: string;
	dropdownStyle: DropdownStyle | undefined;
}

interface CustomThemeColors {
	primary25: string;
}

const getTheme = (theme: Theme): Theme => ({
	...theme,
	borderRadius: 2,
	colors: {
		...theme.colors,
		...({
			primary25: '#FEF4E9',
		} as typeof theme.colors & CustomThemeColors),
		primary: '#FFA726',
	},
});

const customStyles: StylesConfig = {
	control: (base) => ({
		...base,
		boxShadow: '0 !important',
		'&:focus': { borderColor: '#FFA726' },
		'&:active': { borderColor: '#FFA726' },
	}),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class MultiTagSelect<OptionType = any> extends React.PureComponent<Props<OptionType>, State> {
	state: State = {
		id: nanoid(UNIQUE_ID_SIZE),
		dropdownStyle: undefined,
	};

	// Changed to true because isMulti={true} is hardcoded below
	components: SelectComponentsConfig<OptionType, true, GroupBase<OptionType>> = {
		IndicatorSeparator: null,
		DropdownIndicator: () => <span className="icon-down" />,
		ClearIndicator: (props) => (
			<div {...props.innerProps}>
				<span className="icon-close" />
			</div>
		),
		Option: this.props.Option ?? undefined,
		MultiValueLabel: this.props.MultiValueLabel ?
			(props) => {
				const { MultiValueLabel } = this.props;
				if (!MultiValueLabel) throw new Error('Label not defined');
				return <MultiValueLabel {...props} onRemove={this.onRemove.bind(this, props.data)} />;
			} : undefined,
		MultiValueRemove: this.props.MultiValueLabel ? () => null : undefined,
	};

	onRemove = (data: OptionType, event: React.MouseEvent<MouseEvent>) => {
		event.stopPropagation();
		const { fields } = this.props;
		const options = fields.getAll();
		const optionIndex = options.findIndex((_option) => _option === data);
		fields.remove(optionIndex);
	};

	onChange = (items: OnChangeValue<OptionType, true>) => {
		const { changeField, meta, fields, onChange } = this.props;
		const arrayItems = items ? [...items] : [];
		changeField?.(meta.form, fields.name, arrayItems);

		if (onChange) {
			onChange(arrayItems);
		}
	};

	onOpen = () => {
		const { fixed } = this.props;
		if (!fixed) return;

		const { id } = this.state;
		const {
			x: inputPositionX = 0,
			bottom: inputBottom = 0,
			width: inputWidth = 0,
		} = document.getElementById(id)?.getClientRects?.()?.[0] ?? {};

		this.setState(() => ({
			dropdownStyle: {
				top: inputBottom,
				bottom: 'auto',
				left: inputPositionX,
				width: inputWidth,
			},
		}));
	};

	onBlur = (event: React.FocusEvent<HTMLElement>) => {
		const { onBlur } = this.props;
		if (onBlur) {
			onBlur(event);
		}
	};

	render() {
		const {
			placeholder,
			options = [],                 // Default handled here
			label,
			fields,
			containerClassName,
			className = '',               // Default handled here
			getOptionValue,
			getOptionLabel,
			disableErrorMessage = false,  // Default handled here
			meta: { error, warning },
			addonComponent,
			isDisabled = false,           // Default handled here
			menuPlacement = 'auto',       // Default handled here
			fixed,
		} = this.props;
		const { id, dropdownStyle } = this.state;

		let formGroupClassName = 'react-select-container';
		formGroupClassName = containerClassName ? `${formGroupClassName} ${containerClassName}` : formGroupClassName;
		formGroupClassName = addonComponent ? `${formGroupClassName} react-select-container--with-addon` : formGroupClassName;

		let selectClassName = 'react-select-field react-select-field--multi-tag';
		selectClassName = className ? `${selectClassName} ${className}` : selectClassName;
		selectClassName = error ? `${selectClassName} react-select-field--error` : selectClassName;
		selectClassName = fixed ? `${selectClassName} react-select-field--fixed` : selectClassName;

		const styles = fixed ? { ...customStyles, menu: (base) => ({ ...base, ...dropdownStyle }) } : customStyles;

		return (
			<FormGroup className={formGroupClassName}>
				{label && <Label label={label} withMargin={true} />}
				<Select
					className={selectClassName}
					components={this.components}
					getOptionLabel={getOptionLabel}
					getOptionValue={getOptionValue}
					id={id}
					isClearable={!isDisabled}
					isDisabled={isDisabled}
					isMulti={true}
					isSearchable={false}
					menuPlacement={menuPlacement}
					onBlur={this.onBlur}
					onChange={this.onChange}
					onMenuOpen={this.onOpen}
					options={options}
					placeholder={placeholder}
					styles={styles}
					theme={getTheme}
					value={fields.getAll() as PropsValue<OptionType>}
				/>
				{addonComponent?.()}
				<FormControl.Feedback />
				{(!disableErrorMessage) &&
					((error && <span className="help-block"><span className="icon-info" /> {error}</span>) ||
						(warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>))
				}
			</FormGroup>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connector(MultiTagSelect as any) as React.FunctionComponent<WrappedFieldArrayProps<any> & OwnProps<any>>;
